import { motion } from 'framer-motion';
import { FileText, UploadCloud, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';

export interface FileRules {
  /** MIME types, e.g. ['image/jpeg', 'application/pdf']. */
  accept: string[];
  maxBytes: number;
  /** Shown when the type is wrong, e.g. "Use a JPEG, PNG or PDF file." */
  typeMessage: string;
}

export function checkFile(file: File, rules: FileRules): string | null {
  if (!rules.accept.includes(file.type)) return rules.typeMessage;
  if (file.size > rules.maxBytes) return `The file must be ${Math.round(rules.maxBytes / 1024 / 1024)} MB or smaller.`;
  return null;
}

/** Object URL for an image File, revoked when the file changes. */
export function useObjectUrl(file: File | Blob | null): string | null {
  const url = React.useMemo(() => (file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null), [file]);
  React.useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  return url;
}

interface DropzoneProps {
  file: File | null;
  onFile: (f: File | null) => void;
  accept: string[];
  title: string;
  hint: string;
  error?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

/** Drag-and-drop / click-to-pick file input with a preview of the chosen file. */
export function FileDropzone({ file, onFile, accept, title, hint, error, icon, disabled, className }: DropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [drag, setDrag] = React.useState(false);
  const preview = useObjectUrl(file);

  if (file) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className={cn('flex items-center gap-4 rounded-xl border bg-card p-3', className)}>
        <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-secondary">
          {preview ? <img src={preview} alt="" className="size-full object-cover" /> : <FileText className="size-6 text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{file.name}</p>
          <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
          {error && <p className="mt-1 text-xs font-medium text-destructive">{error}</p>}
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={() => onFile(null)} disabled={disabled} title="Remove">
          <X />
        </Button>
      </motion.div>
    );
  }

  return (
    <div className={className}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files[0];
          if (f) onFile(f);
        }}
        className={cn(
          'flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-all disabled:opacity-50',
          drag ? 'scale-[1.01] border-primary bg-primary/5' : 'border-input hover:border-primary/50 hover:bg-secondary/40',
          error && 'border-destructive/60',
        )}
      >
        <motion.span
          animate={drag ? { y: -4, scale: 1.1 } : { y: 0, scale: 1 }}
          className="flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-sun/20 to-brand-navy/20 text-primary-ink [&_svg]:size-6"
        >
          {icon ?? <UploadCloud />}
        </motion.span>
        <span className="text-sm font-semibold">{title}</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept.join(',')}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      {error && <p className="mt-2 text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
